/**
 * The vocabulary of the issues feature, named once so the list, the board, the
 * timeline and the profile sections all say the same words.
 *
 * Position never relies on colour alone: every reading carries a word and a
 * shape (single/double chevron up, a bar, a chevron down), so support and
 * opposition stay apart in greyscale.
 */
import {
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
  LucideIcon,
  Minus,
} from 'lucide-react';
import type { SemanticTone } from '../../../components/ui';
import { POSITION_LABELS } from '../../../types/issues';
import type {
  BalanceOfForces,
  IssueStatus,
  IssueView,
  MovementEntry,
  StanceConfidence,
  StanceSource,
  StanceView,
  SubjectRef,
} from '../../../types/issues';

/* ------------------------------------------------------------------ */
/* Positions                                                           */
/* ------------------------------------------------------------------ */

/** Board order: our side first, so the eye travels from champions to blockers */
export const POSITION_ORDER = [2, 1, 0, -1, -2];

export interface PositionStyle {
  /** "Strongly opposed" */
  label: string;
  /** One word for tight rows */
  short: string;
  icon: LucideIcon;
  tone: SemanticTone;
  /** Soft pill: tint + readable text */
  pill: string;
  /** Icon colour on white */
  text: string;
  /** Solid mark for dots and bars */
  solid: string;
  hex: string;
}

/**
 * Support is emerald, opposition the brand red, neutral stays grey; strength is
 * carried by the shape (double chevron = strongly held) rather than saturation.
 */
const STYLES: Record<number, PositionStyle> = {
  2: {
    label: 'Champion',
    short: 'Champion',
    icon: ChevronsUp,
    tone: 'green',
    pill: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-600',
    solid: 'bg-emerald-500',
    hex: '#10b981',
  },
  1: {
    label: 'Supportive',
    short: 'Supportive',
    icon: ChevronUp,
    tone: 'green',
    pill: 'bg-emerald-50/70 text-emerald-700',
    text: 'text-emerald-500',
    solid: 'bg-emerald-400',
    hex: '#34d399',
  },
  0: {
    label: 'Neutral',
    short: 'Neutral',
    icon: Minus,
    tone: 'neutral',
    pill: 'bg-neutral-100 text-neutral-600',
    text: 'text-neutral-400',
    solid: 'bg-neutral-300',
    hex: '#d4d4d4',
  },
  [-1]: {
    label: 'Opposed',
    short: 'Opposed',
    icon: ChevronDown,
    tone: 'red',
    pill: 'bg-primary-50/70 text-primary-700',
    text: 'text-primary-500',
    solid: 'bg-primary-400',
    hex: '#f87171',
  },
  [-2]: {
    label: 'Strongly opposed',
    short: 'Strongly opp.',
    icon: ChevronsDown,
    tone: 'red',
    pill: 'bg-primary-50 text-primary-700',
    text: 'text-primary-600',
    solid: 'bg-primary-600',
    hex: '#ea2a33',
  },
};

export const clampPosition = (value: number): number => Math.max(-2, Math.min(2, Math.round(value || 0)));

export const positionStyle = (position: number): PositionStyle => STYLES[clampPosition(position)];

export const positionLabel = (position: number): string =>
  POSITION_LABELS[clampPosition(position)] ?? 'Neutral';

/* ------------------------------------------------------------------ */
/* Issue status, priority, dates                                       */
/* ------------------------------------------------------------------ */

export const ISSUE_STATUSES: IssueStatus[] = ['exploring', 'active', 'decided', 'dormant'];

export const STATUS_LABEL: Record<IssueStatus, string> = {
  exploring: 'Exploring',
  active: 'Active',
  decided: 'Decided',
  dormant: 'Dormant',
};

/** Live work is green, a closed decision blue, everything else quiet */
export const statusToneOf = (status: IssueStatus): SemanticTone =>
  status === 'active' ? 'green' : status === 'decided' ? 'blue' : status === 'exploring' ? 'amber' : 'neutral';

export const PRIORITY_LABEL: Record<number, string> = {
  1: 'Watching brief',
  2: 'Low',
  3: 'Normal',
  4: 'High',
  5: 'Critical',
};

export const priorityLabel = (priority: number): string =>
  PRIORITY_LABEL[Math.max(1, Math.min(5, Math.round(priority || 3)))];

const DAY = 86_400_000;

/** "12 Mar 2026" */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDay(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.round((startOfDay(now) - startOfDay(date.getTime())) / DAY);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === new Date(now).getFullYear() ? {} : { year: 'numeric' }),
  });
}

export const startOfDay = (ms: number): number => {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = now - then;
  if (diff < 0) return formatDate(iso);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / DAY);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

/** How close the decision is — "in 9 days", "tomorrow", "3 days ago" */
export function decisionPhrase(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.round((startOfDay(then) - startOfDay(now)) / DAY);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return days < 45 ? `in ${days} days` : `in ${Math.round(days / 30)} months`;
  const past = Math.abs(days);
  return past < 45 ? `${past} days ago` : `${Math.round(past / 30)} months ago`;
}

/** A decision inside a fortnight deserves the eye */
export const decisionIsNear = (iso: string | null | undefined, now: number = Date.now()): boolean => {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  const days = Math.round((startOfDay(then) - startOfDay(now)) / DAY);
  return days >= 0 && days <= 14;
};

/* ------------------------------------------------------------------ */
/* Stances                                                             */
/* ------------------------------------------------------------------ */

export const CONFIDENCES: StanceConfidence[] = ['low', 'medium', 'high'];

export const CONFIDENCE_LABEL: Record<StanceConfidence, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const SOURCES: StanceSource[] = ['stated', 'observed', 'inferred', 'ai'];

export const SOURCE_LABEL: Record<StanceSource, string> = {
  stated: 'Stated',
  observed: 'Observed',
  inferred: 'Inferred',
  ai: 'AI',
};

/** How much weight to give a reading: what it rests on, in one word */
export const SOURCE_HINT: Record<StanceSource, string> = {
  stated: 'They said so themselves',
  observed: 'Seen in what they did',
  inferred: 'Reasoned from context',
  ai: 'Proposed by AI and accepted',
};

export const subjectKey = (subject: { type: string; id: string }): string => `${subject.type}:${subject.id}`;

export const subjectHref = (subject: { type: string; id: string }): string =>
  subject.type === 'organization' ? `/organizations/${subject.id}` : `/stakeholders/${subject.id}`;

export const issueHref = (issue: { slug?: string | null; id: string }): string =>
  `/issues/${issue.slug || issue.id}`;

/**
 * Stances come back as history; one row per subject is what a board shows.
 * Keeps the most recent reading (by `asOf`, then `createdAt`) for each subject.
 */
export function currentStances(stances: StanceView[]): StanceView[] {
  const latest = new Map<string, StanceView>();
  stances.forEach((stance) => {
    const key = subjectKey(stance.subject);
    const held = latest.get(key);
    if (!held || stanceTime(stance) >= stanceTime(held)) latest.set(key, stance);
  });
  return Array.from(latest.values());
}

export const stanceTime = (stance: StanceView): number => {
  const asOf = new Date(stance.asOf || stance.createdAt || 0).getTime();
  return Number.isNaN(asOf) ? 0 : asOf;
};

/** Influence first — a board sorted by name hides who matters */
export function sortStances(stances: StanceView[], by: 'influence' | 'recent' | 'name'): StanceView[] {
  const copy = [...stances];
  if (by === 'name') return copy.sort((a, b) => a.subject.label.localeCompare(b.subject.label));
  if (by === 'recent') return copy.sort((a, b) => stanceTime(b) - stanceTime(a));
  return copy.sort(
    (a, b) => (b.issueInfluence || 0) - (a.issueInfluence || 0) || a.subject.label.localeCompare(b.subject.label),
  );
}

/* ------------------------------------------------------------------ */
/* Movement                                                            */
/* ------------------------------------------------------------------ */

export type MovementDirection = 'towards' | 'away' | 'first';

export function movementDirection(entry: MovementEntry): MovementDirection {
  if (entry.from === null || entry.from === undefined) return 'first';
  return entry.towardsUs ? 'towards' : 'away';
}

export const DIRECTION_LABEL: Record<MovementDirection, string> = {
  towards: 'Towards us',
  away: 'Away from us',
  first: 'First reading',
};

export const DIRECTION_TONE: Record<MovementDirection, SemanticTone> = {
  towards: 'green',
  away: 'red',
  first: 'neutral',
};

/** "3 moved · 2 towards us", or nothing at all when nobody moved */
export function movementSummary(moved: number, towards: number): string {
  if (!moved) return '';
  const head = `${moved} moved`;
  if (!towards) return head;
  return `${head} · ${towards} towards us`;
}

/* ------------------------------------------------------------------ */
/* Balance of forces                                                   */
/* ------------------------------------------------------------------ */

export interface BalanceShares {
  support: number;
  neutral: number;
  oppose: number;
  /** No weight recorded at all — the bar has nothing honest to show */
  empty: boolean;
}

/**
 * Influence-weighted shares of the three camps as percentages that add to 100.
 * A headcount would be misleading: one regulator can outweigh twenty supporters.
 */
export function balanceShares(balance: BalanceOfForces | null | undefined): BalanceShares {
  const support = Math.max(0, balance?.support ?? 0);
  const neutral = Math.max(0, balance?.neutral ?? 0);
  const oppose = Math.max(0, balance?.oppose ?? 0);
  const total = support + neutral + oppose;
  if (!total) return { support: 0, neutral: 0, oppose: 0, empty: true };
  return {
    support: (support / total) * 100,
    neutral: (neutral / total) * 100,
    oppose: (oppose / total) * 100,
    empty: false,
  };
}

/** −100 … +100 mapped onto a 0–100 rail, so the pointer sits where it belongs */
export const indexOffset = (index: number): number => Math.max(0, Math.min(100, (Math.max(-100, Math.min(100, index || 0)) + 100) / 2));

export const formatIndex = (index: number): string => {
  const value = Math.round(index || 0);
  return value > 0 ? `+${value}` : `${value}`;
};

/** One sentence a screen reader can read instead of the bar */
export function balanceDescription(balance: BalanceOfForces | null | undefined): string {
  const shares = balanceShares(balance);
  if (shares.empty) return 'No positions recorded yet';
  return `Balance ${formatIndex(balance?.index ?? 0)} of 100 — support ${Math.round(shares.support)}%, neutral ${Math.round(
    shares.neutral,
  )}%, opposition ${Math.round(shares.oppose)}%`;
}

export const totalPositions = (balance: BalanceOfForces | null | undefined): number => {
  const counts = balance?.counts;
  if (!counts) return 0;
  return counts.champion + counts.supportive + counts.neutral + counts.opposed + counts.stronglyOpposed;
};

/** Named counts in board order, so every surface lists them the same way */
export function positionCounts(balance: BalanceOfForces | null | undefined): { position: number; count: number }[] {
  const counts = balance?.counts;
  return [
    { position: 2, count: counts?.champion ?? 0 },
    { position: 1, count: counts?.supportive ?? 0 },
    { position: 0, count: counts?.neutral ?? 0 },
    { position: -1, count: counts?.opposed ?? 0 },
    { position: -2, count: counts?.stronglyOpposed ?? 0 },
  ];
}

/* ------------------------------------------------------------------ */
/* Sorting issues                                                      */
/* ------------------------------------------------------------------ */

export type IssueSort = 'priority' | 'decision' | 'balance' | 'movement' | 'recent';

export const ISSUE_SORTS: { value: IssueSort; label: string }[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'decision', label: 'Decision' },
  { value: 'balance', label: 'Balance' },
  { value: 'movement', label: 'Movement' },
  { value: 'recent', label: 'Recent' },
];

const time = (iso: string | null | undefined): number => {
  const value = new Date(iso || 0).getTime();
  return Number.isNaN(value) ? 0 : value;
};

export function sortIssues(issues: IssueView[], by: IssueSort): IssueView[] {
  const copy = [...issues];
  switch (by) {
    case 'decision':
      // Undated issues sink rather than pretending to be urgent
      return copy.sort((a, b) => {
        const left = a.decisionDate ? time(a.decisionDate) : Infinity;
        const right = b.decisionDate ? time(b.decisionDate) : Infinity;
        return left - right || b.priority - a.priority;
      });
    case 'balance':
      return copy.sort((a, b) => (a.balance?.index ?? 0) - (b.balance?.index ?? 0));
    case 'movement':
      return copy.sort((a, b) => (b.recentMovements ?? 0) - (a.recentMovements ?? 0));
    case 'recent':
      return copy.sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
    case 'priority':
    default:
      return copy.sort(
        (a, b) =>
          b.priority - a.priority ||
          (a.decisionDate ? time(a.decisionDate) : Infinity) - (b.decisionDate ? time(b.decisionDate) : Infinity) ||
          a.title.localeCompare(b.title),
      );
  }
}

export const subjectRefOf = (subject: SubjectRef): SubjectRef => ({
  type: subject.type,
  id: subject.id,
  label: subject.label,
});

/** "community-relations" → "Community relations", for keys nobody labelled */
export const readableKey = (key: string): string => {
  const words = key.replace(/[-_]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
};
